import { useEffect, useRef, useState } from 'react'
import { marked } from 'marked'
import { streamNarrative } from '../api/client'
import { track } from '../umami'

interface Props {
  siteId: string
  siteName: string
}

function narrativeToHtml(text: string): string {
  return marked.parse(text, { breaks: true }) as string
}

export default function NarrativeStream({ siteId, siteName }: Props) {
  const [html, setHtml] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    // Abort any previous stream
    abortRef.current?.()
    setHtml(null)
    setError(null)

    let rawText = ''
    let started = false

    abortRef.current = streamNarrative(siteId, {
      onCached(text) {
        setHtml(narrativeToHtml(text))
        track('narrative_cached', { site_id: siteId, site_name: siteName })
      },
      onDelta(text) {
        if (!started) {
          started = true
          track('narrative_generated', {
            site_id: siteId,
            site_name: siteName,
          })
        }
        rawText += text
        setHtml(narrativeToHtml(rawText))
      },
      onDone() {
        // Final render already happened
      },
      onError() {
        if (!started) {
          setError('Could not generate narrative.')
        }
      },
    })

    return () => {
      abortRef.current?.()
    }
  }, [siteId, siteName])

  if (error) {
    return <p className="narrative-error">{error}</p>
  }

  if (html === null) {
    return (
      <>
        <div className="skeleton-block skeleton-line" />
        <div className="skeleton-block skeleton-line" />
        <div className="skeleton-block skeleton-line skeleton-line-short" />
        <div className="skeleton-block skeleton-line" />
        <div className="skeleton-block skeleton-line" />
        <div className="skeleton-block skeleton-line skeleton-line-short" />
      </>
    )
  }

  return <div dangerouslySetInnerHTML={{ __html: html }} />
}
